import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Shield, ChevronRight, MapPin, Users, AlertTriangle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const ALIGNMENT_STYLE = {
  clean: { color: '#4ADE80', label: 'CLEAN', bg: '#4ADE80' },
  compromised: { color: '#C9A84C', label: 'COMPROMISED', bg: '#C9A84C' },
  corrupt: { color: '#DC2626', label: 'CORRUPT', bg: '#DC2626' },
};

const TaskForceView = ({ onBack }) => {
  const [atlanta, setAtlanta] = useState(null);
  const [interstate, setInterstate] = useState(null);
  const [tab, setTab] = useState('atlanta');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [aRes, iRes] = await Promise.all([
          fetch(`${API}/api/police/taskforce/atlanta`),
          fetch(`${API}/api/police/interstate`),
        ]);
        if (aRes.ok) setAtlanta(await aRes.json());
        if (iRes.ok) setInterstate(await iRes.json());
      } catch (e) {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/20 text-xs text-center py-8">Loading...</p>;

  return (
    <div className="space-y-4" data-testid="task-force-view">
      <button onClick={onBack} className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px] tracking-widest hover:text-[#1E90FF]" data-testid="tf-back">BACK</button>

      <div className="text-center">
        <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1E90FF]">OPERATION LOW TIDE</h3>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest">INTERSTATE ORGANIZED CRIME TASK FORCE</p>
      </div>

      {/* Tab selector */}
      <div className="flex gap-1">
        <button onClick={() => setTab('atlanta')}
          className={`flex-1 px-3 py-2 rounded-lg font-['Outfit'] text-[9px] tracking-wider border transition-all ${
            tab === 'atlanta' ? 'bg-[#1E90FF]/10 text-[#1E90FF] border-[#1E90FF]/30' : 'text-[#E8E0D0]/30 border-[#1A2535]'
          }`} data-testid="tab-atlanta">
          ATLANTA (AMDOC)
        </button>
        <button onClick={() => setTab('interstate')}
          className={`flex-1 px-3 py-2 rounded-lg font-['Outfit'] text-[9px] tracking-wider border transition-all ${
            tab === 'interstate' ? 'bg-[#1E90FF]/10 text-[#1E90FF] border-[#1E90FF]/30' : 'text-[#E8E0D0]/30 border-[#1A2535]'
          }`} data-testid="tab-interstate">
          INTERSTATE ({interstate?.total_states || 5} STATES)
        </button>
      </div>

      {/* Atlanta Task Force */}
      {tab === 'atlanta' && atlanta && (
        <div className="space-y-3">
          {/* Hero banner */}
          <div className="relative h-44 rounded-xl overflow-hidden">
            <img src={atlanta.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              <p className="font-['Outfit'] text-[#DC2626] text-[8px] tracking-[0.3em] font-bold">{atlanta.street_name}</p>
              <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">{atlanta.name}</h4>
              <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[9px]">{atlanta.jurisdiction}</p>
            </div>
          </div>

          {/* Specialties */}
          <div className="bg-[#111820] border border-[#1E90FF]/15 rounded-xl p-3">
            <p className="font-['Outfit'] text-[#1E90FF] text-[8px] tracking-widest mb-2">ENFORCEMENT SPECIALTIES</p>
            <div className="flex flex-wrap gap-1.5">
              {atlanta.specialties.map((s, i) => (
                <span key={i} className="px-2 py-1 bg-[#1E90FF]/5 border border-[#1E90FF]/15 rounded text-[#E8E0D0]/50 font-['Outfit'] text-[8px]">{s}</span>
              ))}
            </div>
          </div>

          {/* Roster */}
          <div>
            <p className="font-['Outfit'] text-[#DC2626] text-[9px] tracking-[0.3em] mb-2">TASK FORCE ROSTER</p>
            <div className="space-y-2">
              {atlanta.roster.map((member, i) => {
                const align = ALIGNMENT_STYLE[member.alignment] || ALIGNMENT_STYLE.clean;
                return (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-[#0a0e12] border border-[#1A2535] rounded-xl overflow-hidden"
                    data-testid={`roster-${i}`}>
                    <div className="flex gap-3 p-2.5">
                      {member.image && (
                        <div className="w-16 h-20 rounded-lg overflow-hidden shrink-0 border border-[#1A2535]">
                          <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h5 className="font-['Outfit'] text-xs font-bold text-[#E8E0D0]">{member.name}</h5>
                            <p className="font-['Outfit'] text-[9px] text-[#1E90FF]">{member.position}</p>
                          </div>
                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[6px] tracking-widest font-['Outfit'] font-bold"
                            style={{ color: align.color, background: align.bg + '15', border: `1px solid ${align.bg}30` }}>
                            {align.label}
                          </span>
                        </div>
                        <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/30 mt-1">{member.specialty}</p>
                        {member.alignment_note && (
                          <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/15 italic mt-0.5">{member.alignment_note}</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Interstate Network */}
      {tab === 'interstate' && interstate && (
        <div className="space-y-3">
          <div className="relative h-36 rounded-xl overflow-hidden">
            <img src="https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/425274f7001318e248300912342eaccb45e04a9ffdf45cc2f28e077a9d487928.jpeg"
              alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
            <div className="absolute bottom-3 left-4">
              <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">{interstate.operation}</h4>
              <p className="font-['Outfit'] text-[#1E90FF] text-[9px]">{interstate.total_states} state task forces</p>
            </div>
          </div>

          <div className="space-y-2">
            {Object.entries(interstate.states).map(([sid, state], i) => (
              <motion.div key={sid}
                initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="bg-[#111820] border border-[#1A2535] rounded-xl p-3"
                data-testid={`state-${sid}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      state.hq ? 'bg-[#FFD700]/15 border border-[#FFD700]/30' : 'bg-[#1E90FF]/10 border border-[#1E90FF]/20'
                    }`}>
                      <span className={`font-['Outfit'] text-[10px] font-bold ${state.hq ? 'text-[#FFD700]' : 'text-[#1E90FF]'}`}>{sid.toUpperCase()}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h5 className="font-['Outfit'] text-xs font-bold text-[#E8E0D0]">{state.name}</h5>
                        {state.hq && <span className="font-['Outfit'] text-[6px] tracking-widest text-[#FFD700] bg-[#FFD700]/10 border border-[#FFD700]/20 px-1.5 py-0.5 rounded">HQ</span>}
                      </div>
                      <p className="font-['Outfit'] text-[8px] text-[#E8E0D0]/30">{state.specialty}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-2 pl-10">
                  <p className="font-['Outfit'] text-[7px] text-[#C9A84C]">{state.commander}</p>
                  <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/25">{state.intel_detective}</p>
                  <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/25">{state.narcotics}</p>
                  <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/25">{state.tactical}</p>
                  <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/25">{state.financial}</p>
                  <p className="font-['Outfit'] text-[7px] text-[#DC2626]/40">{state.compromised}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskForceView;
